import React, { useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import {
  BookAvailabilityV2ResponseModel,
  BookOfferResponseModel
} from '../../../../../shared/ui-models/book.ui-model';
import { ReservationMode } from '../../../../../shared/enums/reservation-mode.enum';
import {
  CHIP,
  EMPTY_STATE,
  FIELD,
  LABEL,
  OUTLINE_BUTTON,
  SECTION_TITLE,
  formatMoney,
  selectProps
} from '../../reservation-create.ui';
import { StayDatesPicker } from '../stay-dates-picker/stay-dates-picker.component';

interface Option {
  value: string;
  label: string;
}

export interface DatesSectionProps {
  data: {
    unitType?: UnitTypeUiModel;
    availability?: BookAvailabilityV2ResponseModel;
    dates?: Date[];
    bookOffer?: BookOfferResponseModel;
    roomId: string;
    overridePrice?: number;
    promoCode: string;
    discountAmount?: number;
    discountFees?: string[];
    promotion: { options: Option[]; isLoading: boolean };
  };
  eventHandlers: {
    onDatesChange: (dates: Date[]) => void;
    onRoomChange: (roomId: string, roomName: string) => void;
    onOverridePriceChange: (price?: number) => void;
    onPromoCodeChange: (promoCode: string) => void;
    onPromoMenuOpen: () => void;
    onDiscountChange: (amount?: number, fees?: string[]) => void;
  };
}

export const DatesSection: React.FC<DatesSectionProps> = ({
  data: {
    unitType,
    availability,
    dates,
    bookOffer,
    roomId,
    overridePrice,
    promoCode,
    discountAmount,
    discountFees,
    promotion
  },
  eventHandlers: {
    onDatesChange,
    onRoomChange,
    onOverridePriceChange,
    onPromoCodeChange,
    onPromoMenuOpen,
    onDiscountChange
  }
}) => {
  const [overrideDraft, setOverrideDraft] = useState(overridePrice ? String(overridePrice) : '');
  const [promoDraft, setPromoDraft] = useState(promoCode);
  const [syncedPromoCode, setSyncedPromoCode] = useState(promoCode);
  if (promoCode !== syncedPromoCode) {
    setSyncedPromoCode(promoCode);
    setPromoDraft(promoCode);
  }
  const [discountDraft, setDiscountDraft] = useState(discountAmount ? String(discountAmount) : '');
  const [feesDraft, setFeesDraft] = useState<Option[]>(
    (discountFees || []).map((fee) => ({ value: fee, label: fee === 'Unit Price' ? 'Rent' : fee }))
  );

  if (!unitType) {
    return (
      <>
        <h2 className={SECTION_TITLE}>Dates & price</h2>
        <div className={EMPTY_STATE}>Select a unit first</div>
      </>
    );
  }

  const isDaily = unitType.reservationMode === ReservationMode.DAILY;
  const hasDates = !!dates && dates.length === 2;
  const isOverrideLocked = !!promoCode || discountAmount !== undefined;
  const isPromoLocked = overridePrice !== undefined || discountAmount !== undefined;
  const isDiscountLocked = overridePrice !== undefined || !!promoCode;
  const roomOptions = (bookOffer?.availableRooms || []).map((room) => ({
    value: room.unitId,
    label: room.shortName
  }));
  const feeOptions: Option[] = [{ value: 'Unit Price', label: 'Rent' }].concat(
    (unitType.fees || []).map((fee) => ({ value: fee.name || '', label: fee.name || '' }))
  );

  return (
    <>
      <h2 className={SECTION_TITLE}>Dates & price</h2>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)] items-end gap-[16px]">
        <StayDatesPicker
          data={{
            checkIn: dates?.[0],
            checkOut: dates?.[1],
            checkInBlocked: availability?.checkInBlockedDates || [],
            checkOutBlocked: availability?.checkOutBlockedDates || [],
            basePrice: isDaily ? unitType.unitPrice : undefined,
            customPrices: isDaily ? unitType.customPrices : undefined,
            isLoading: !availability
          }}
          eventHandlers={{ onChange: onDatesChange }}
        />
        <label className={LABEL}>
          Room
          <Select<Option, false>
            {...selectProps}
            options={roomOptions}
            value={roomOptions.find((option) => option.value === roomId) || null}
            onChange={(value) => onRoomChange(value?.value || '', value?.label || '')}
            placeholder={hasDates ? 'Select room' : 'Pick dates first'}
            isDisabled={!hasDates}
            noOptionsMessage={() => 'No rooms available for these dates'}
          />
        </label>
      </div>
      {!!unitType.ratePlans?.length && (
        <div className="mt-[16px] flex flex-wrap gap-[6px]">
          {unitType.ratePlans.map((ratePlan) => (
            <span key={ratePlan.name} className={`${CHIP} bg-primary-50 text-primary-700`}>
              {ratePlan.name} · {ratePlan.amount}% off
            </span>
          ))}
        </div>
      )}

      <div className="mt-[32px] grid grid-cols-1 md:grid-cols-2 gap-x-[32px] gap-y-[20px]">
        <div className="flex items-end gap-[8px]">
          <label className={`${LABEL} flex-1`}>
            Override base price
            <input
              className={FIELD}
              type="number"
              min={0}
              value={overrideDraft}
              placeholder={
                unitType.unitPrice !== undefined
                  ? formatMoney(unitType.unitPrice, unitType.currency)
                  : ''
              }
              disabled={isOverrideLocked}
              onChange={(event) => setOverrideDraft(event.target.value)}
            />
          </label>
          <button
            type="button"
            className={OUTLINE_BUTTON}
            disabled={
              isOverrideLocked ||
              !hasDates ||
              !overrideDraft ||
              Number(overrideDraft) === overridePrice
            }
            onClick={() => onOverridePriceChange(Number(overrideDraft))}
          >
            Apply
          </button>
          {overridePrice !== undefined && (
            <button
              type="button"
              className={OUTLINE_BUTTON}
              onClick={() => {
                setOverrideDraft('');
                onOverridePriceChange(undefined);
              }}
            >
              Remove
            </button>
          )}
        </div>

        <div className="flex items-end gap-[8px]">
          <label className={`${LABEL} flex-1 min-w-0`}>
            Promo code
            <CreatableSelect<Option, false>
              {...selectProps}
              options={promotion.options}
              value={promoDraft ? { value: promoDraft, label: promoDraft } : null}
              onMenuOpen={onPromoMenuOpen}
              onChange={(value) => {
                const next = value?.value || '';
                setPromoDraft(next);
                if (!next && promoCode) onPromoCodeChange('');
              }}
              onCreateOption={(value) => setPromoDraft(value.trim())}
              formatCreateLabel={(value) => `Use “${value}”`}
              isLoading={promotion.isLoading}
              placeholder="Select or type a code"
              isDisabled={isPromoLocked}
              isClearable
            />
          </label>
          <button
            type="button"
            className={OUTLINE_BUTTON}
            disabled={isPromoLocked || !hasDates || promoDraft.trim() === promoCode}
            onClick={() => onPromoCodeChange(promoDraft.trim())}
          >
            Apply
          </button>
        </div>

        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-[8px]">
          <label className={LABEL}>
            Discount amount
            <input
              className={FIELD}
              type="number"
              min={0}
              value={discountDraft}
              disabled={isDiscountLocked}
              onChange={(event) => setDiscountDraft(event.target.value)}
            />
          </label>
          <label className={`${LABEL} min-w-0`}>
            Discount applies to
            <Select<Option, true>
              {...selectProps}
              options={feeOptions}
              value={feesDraft}
              onChange={(value) => setFeesDraft([...(value || [])])}
              placeholder="Select fees"
              isDisabled={isDiscountLocked}
              isMulti
              isSearchable
            />
          </label>
          <div className="flex items-center gap-[8px]">
            <button
              type="button"
              className={OUTLINE_BUTTON}
              disabled={isDiscountLocked || !hasDates || !discountDraft || !feesDraft.length}
              onClick={() =>
                onDiscountChange(
                  Number(discountDraft),
                  feesDraft.map((fee) => fee.value)
                )
              }
            >
              Apply
            </button>
            {discountAmount !== undefined && (
              <button
                type="button"
                className={OUTLINE_BUTTON}
                onClick={() => {
                  setDiscountDraft('');
                  setFeesDraft([]);
                  onDiscountChange(undefined, undefined);
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
